import {
  enumPurchaseOrderStatus,
  enumSalesOrderStatus,
  PurchaseOrderStatus,
  SalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { Badge, MaybeTruncateMiddle, ThemeColor } from '@rematter/ui-web';
import { cn, formatMoney, humanReadableAddress } from '@rematter/ui-web/lib';
import { LucideTag } from 'lucide-react';

import { LinkedOrderType } from '../../_state/linkedOrdersTab.state';
import { OrderRecordForLink } from '../../types/brokeredOrder';

interface LinkOrderDialogProps {
  order: OrderRecordForLink;
  orderType: LinkedOrderType;
  isSelected?: boolean;
  onClick?: () => void;
}

const colorBySalesOrderStatus: Record<SalesOrderStatus, ThemeColor> = {
  [enumSalesOrderStatus.Draft]: 'neutral',
  [enumSalesOrderStatus.Open]: 'primary',
  [enumSalesOrderStatus.Finished]: 'success',
  [enumSalesOrderStatus.Voided]: 'failure',
};

const colorByPurchaseOrderStatus: Record<PurchaseOrderStatus, ThemeColor> = {
  [enumPurchaseOrderStatus.Draft]: 'neutral',
  [enumPurchaseOrderStatus.Open]: 'primary',
  [enumPurchaseOrderStatus.Closed]: 'success',
  [enumPurchaseOrderStatus.Voided]: 'failure',
};

export const LinkOrderCard = ({
  order,
  orderType,
  isSelected,
  onClick,
}: LinkOrderDialogProps) => {
  const rawAddress = order.address?.[0];
  const accountAddress = rawAddress
    ? {
        streetAddress: [rawAddress.street1, rawAddress.street2]
          .filter(Boolean)
          .join(' '),
        city: rawAddress.city ?? '',
        state: rawAddress.state ?? '',
        postalCode: rawAddress.postalCode ?? '',
      }
    : null;

  const badgeColor =
    orderType === 'salesOrder'
      ? colorBySalesOrderStatus[order.status as SalesOrderStatus]
      : colorByPurchaseOrderStatus[order.status as PurchaseOrderStatus];

  const itemsCount = order.lineItems?.length;

  return (
    <div
      key={order.id}
      className={cn(
        '@LinkOrderCard',
        'flex flex-col justify-center gap-1 border-1 border-neutral/10',
        'cursor-pointer rounded-lg bg-white px-4 py-3 hover:bg-neutral/5',
        isSelected && 'border-2 border-primary',
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Select order ${order.documentNumber}`}
      aria-pressed={isSelected}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="text-lg font-medium">{order.documentNumber}</span>
        <Badge color={badgeColor} size="small">
          {order.status}
        </Badge>
      </div>
      <div className="flex flex-col justify-center gap-1 text-sm text-neutral/60">
        <div className="text-base text-neutral">{order.accountName}</div>
        {accountAddress && (
          <MaybeTruncateMiddle text={humanReadableAddress(accountAddress)} />
        )}
        <div className="flex gap-3">
          <div className="flex items-center gap-1">
            <LucideTag size={16} /> {itemsCount}
          </div>

          <div>
            {order.total &&
              formatMoney(order.total?.amount, order.total?.currencyCode)}
          </div>
        </div>
      </div>
    </div>
  );
};
